import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { generateFaviconIco } from './icoGenerator';
import { IOS_PLATFORM_IDS, IOS_FOLDER } from '../data/platforms/index';

/**
 * Build a combined Contents.json for the iOS asset catalog
 * from all selected iphone + ipad icons.
 */
function buildIosContentsJson(icons) {
  const images = icons.map((i) => ({
    filename: i.iconDef.filename,
    idiom: i.iconDef.idiom,
    scale: `${i.iconDef.scale}x`,
    size: `${i.iconDef.pointSize}x${i.iconDef.pointSize}`,
  }));
  return { images, info: { version: 1, author: 'xcode' } };
}

/**
 * Build a Contents.json for a single Apple platform (macOS, watchOS).
 */
function buildAppleContentsJson(platform, icons) {
  if (!platform.generateContentsJson) return null;
  // Build from actual generated icons rather than the full platform spec
  const images = icons.map((i) => ({
    filename: i.iconDef.filename,
    idiom: i.iconDef.idiom,
    role: i.iconDef.role,
    scale: `${i.iconDef.scale}x`,
    size: `${i.iconDef.pointSize}x${i.iconDef.pointSize}`,
  }));
  return { images, info: { version: 1, author: 'xcode' } };
}

/**
 * Core ZIP builder — used by all download functions.
 */
export async function buildZip(icons, options = {}) {
  const { appName = 'AppIcon', platforms = {}, isIosBundle = false } = options;
  const zip = new JSZip();

  // Add all icon files
  for (const icon of icons) {
    zip.folder(icon.zipFolder).file(icon.filename, icon.blob);
  }

  // ── iOS: combined Contents.json for iphone + ipad ──────────────────────
  const iosIcons = icons.filter((i) => IOS_PLATFORM_IDS.includes(i.platformId));
  if (iosIcons.length > 0 || isIosBundle) {
    const contents = buildIosContentsJson(iosIcons);
    zip.folder(IOS_FOLDER).file('Contents.json', JSON.stringify(contents, null, 2));
  }

  // ── macOS & watchOS: individual Contents.json ─────────────────────────
  for (const platformId of ['macos', 'watchos']) {
    const platform = platforms[platformId];
    if (!platform) continue;
    const platIcons = icons.filter((i) => i.platformId === platformId);
    if (platIcons.length === 0) continue;
    const contents = buildAppleContentsJson(platform, platIcons);
    if (contents) {
      zip.folder(platform.folder).file('Contents.json', JSON.stringify(contents, null, 2));
    }
  }

  // ── Web: favicon.ico + manifest + HTML snippet ──────────────────────────
  const webPlatform = platforms['web'];
  if (webPlatform) {
    const faviconSizes = [16, 32, 48];
    const faviconBlobs = icons
      .filter((i) => i.platformId === 'web' && faviconSizes.includes(i.size))
      .map((i) => ({ size: i.size, blob: i.blob }))
      .sort((a, b) => a.size - b.size);

    if (faviconBlobs.length > 0) {
      const icoBlob = await generateFaviconIco(faviconBlobs);
      zip.folder('web').file('favicon.ico', icoBlob);
    }

    if (webPlatform.extraFiles) {
      for (const extra of webPlatform.extraFiles) {
        zip.folder(extra.folder).file(extra.filename, extra.generate(appName));
      }
    }
  }

  return zip;
}

/**
 * Download all generated icons as a single ZIP.
 */
export async function downloadAllZip(icons, options = {}) {
  const { zipName = 'app-icons', appName = 'AppIcon', platforms = {} } = options;
  const zip = await buildZip(icons, { appName, platforms });
  const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
  saveAs(blob, `${zipName}.zip`);
}

/**
 * Download a single platform's icons as a ZIP.
 * When isIosBundle is true, icons already contain iphone+ipad combined.
 */
export async function downloadPlatformZip(icons, platformId, options = {}) {
  const { zipName = 'app-icons', appName = 'AppIcon', platforms = {}, isIosBundle = false } = options;
  const zip = await buildZip(icons, { appName, platforms, isIosBundle });
  const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
  saveAs(blob, `${zipName}-${platformId}.zip`);
}

/**
 * Download a single icon file directly.
 */
export function downloadSingleIcon(icon) {
  saveAs(icon.blob, icon.filename);
}
